import { COMPONENT_STATE_ARGS, Metadata, pick } from '../../../utils/spds/metadata';
import { tracked } from '@glimmer/tracking';
import Super from './keyboard-navigable.gjs';

const METADATA = Metadata.define({
  description: `
The selectable-menu base is an abstract core component that layers single-value selection on top of the keyboard-navigable
navigation engine. It is the shared foundation for menu-style components that track a "current value" and reflect it on a
selected option (filter buttons, dropdown/search inputs, and similar list-with-selection components).

It does not render a template of its own. It provides the value/selection plumbing that would otherwise be duplicated across
consumers: a tracked value (with a configurable default and normalization), current-value bookkeeping, and the
\`aria-selected\` selection writer.

### Value model

\`value\` is derived from a tracked backing field falling back to \`defaultValue\`, then run through \`normalizeValue\`. Setting
\`value\` records it as the \`currentValue\` (the value currently reflected by the menu's \`aria-selected\` state). \`isCurrentValue\`
compares against \`currentValue\` so selection is idempotent.

### Option model

The option model itself lives on \`Spds::Core::KeyboardNavigable\` - registering the menu as the \`optionSource\` is what
supplies \`registeredOptions\`, and \`navigableOptions\` narrows those to the visible ones. This class adds one hook on top:

* \`optionMatchesValue(option, value)\`: whether an option corresponds to a value (e.g. by \`data-value\` and/or text content).

### Configurable surface

* \`defaultValue\` (getter): the value used when none has been set. Defaults to \`undefined\`.
* \`normalizeValue(value)\`: coerces the resolved value (e.g. to an empty string). Defaults to identity.
* \`optionMatchesValue\`: the value adapter described above.

### Adopting in a new component

1. Extend this class instead of \`keyboard-navigable\` when the component has a single selected value.
2. Register the menu via the base-class registration hooks so \`optionSource\` is populated.
3. Override \`defaultValue\` (and \`normalizeValue\` if needed) to match the component's value semantics.
4. Call \`setSelection(value)\` from \`initialize\` to reflect the initial selection.
  `,
  extends: [pick(COMPONENT_STATE_ARGS, ['disabled'])],
  name: 'Selectable Menu',
});

/**
 * Abstract base that adds single-value selection bookkeeping to the keyboard-navigable engine.
 *
 * @argument {boolean} disabled when true, all keyboard interaction is ignored
 *
 * @property {*} value the component's effective value (backing field, falling back to defaultValue, then normalized)
 * @property {*} currentValue the value currently reflected by the menu's aria-selected state
 * @property {Element} selected the option element that is currently selected
 */
export default class SpdsCoreSelectableMenu extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  currentValue;

  @tracked _value;
  @tracked selected;

  /**
   * the value used when none has been set; override to provide a component default
   * @return {*}
   */
  get defaultValue() {
    return undefined;
  }

  /**
   * the component's effective value
   * @return {*}
   */
  get value() {
    return this.normalizeValue(this._value ?? this.defaultValue);
  }

  set value(value) {
    this.currentValue = value;
    this._value = value;
  }

  /**
   * coerces the resolved value; override to normalize (e.g. to an empty string)
   * @param {*} value
   * @return {*}
   */
  normalizeValue(value) {
    return value;
  }

  /**
   * whether an option corresponds to a value; override to match by additional attributes
   * @param {Element} option
   * @param {*} value
   * @return {boolean}
   */
  optionMatchesValue(option, value) {
    return option.dataset.value === value;
  }

  /**
   * finds the registered option matching a value
   * @param {*} value
   * @return {Element|undefined}
   */
  findOptionByValue(value) {
    return this.registeredOptions.find(option => this.optionMatchesValue(option, value));
  }

  /**
   * whether the value is already the current selection
   * @param {*} value
   * @return {boolean}
   */
  isCurrentValue(value) {
    return value === this.currentValue;
  }

  /**
   * clears the active (focused) state from every registered option
   */
  removeFocus() {
    this.registeredOptions.forEach(option => option.classList.remove('active'));
  }

  /**
   * reflects a new selection: clears the previous aria-selected option, updates the value,
   * and marks the matching option as selected. No-op when the value is already current.
   * @param {*} value
   */
  setSelection(value) {
    if (this.isCurrentValue(value)) return;

    let previous = this.findOptionByValue(this.currentValue);
    previous?.setAttribute('aria-selected', 'false');

    this.value = value;

    this.selected = this.findOptionByValue(value);
    this.selected?.setAttribute('aria-selected', 'true');
  }
}

export const CORE_SELECTABLE_MENU_METADATA = SpdsCoreSelectableMenu.metadata;
